import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { platformAPI } from '../../lib/platformApi';
import { ArrowRight, Eye, EyeOff } from 'lucide-react';

const businessTypes = [
  { value: 'lending', label: 'Lending / BNPL' },
  { value: 'payroll_eor', label: 'Payroll / EOR' },
  { value: 'marketplace', label: 'Marketplace / E-commerce' },
  { value: 'gig_economy', label: 'Gig Economy / Freelance' },
  { value: 'savings', label: 'Savings / Investment' },
  { value: 'remittance', label: 'Remittance / Cross-border' },
  { value: 'insurance', label: 'Insurance' },
  { value: 'logistics', label: 'Logistics / Delivery' },
  { value: 'entertainment', label: 'Entertainment / Media' },
  { value: 'education', label: 'Education / EdTech' },
  { value: 'health', label: 'Health / Wellness' },
  { value: 'real_estate', label: 'Real Estate / PropTech' },
  { value: 'agriculture', label: 'Agriculture / AgriTech' },
  { value: 'other', label: 'Other' },
];

export default function PlatformSignup() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [otherType, setOtherType] = useState('');
  const [form, setForm] = useState({
    business_name: '',
    email: '',
    password: '',
    business_type: '',
  });

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (form.password.length < 8) {
      toast.error('Password must be at least 8 characters');
      return;
    }
    const submitData = { ...form };
    if (form.business_type === 'other' && otherType.trim()) {
      submitData.business_type = otherType.trim();
    }
    setLoading(true);
    try {
      const { data } = await platformAPI.signup(submitData);
      localStorage.setItem('platform_first_api_key', data.credentials.test_api_key);
      // Auto-login after signup
      const loginRes = await platformAPI.login({ email: form.email, password: form.password });
      localStorage.setItem('platform_token', loginRes.data.access_token);
      localStorage.setItem('platform_tenant', JSON.stringify(loginRes.data.tenant));
      toast.success('Account created! Your sandbox is ready.');
      navigate('/platform/get-started');
    } catch (err) {
      const msg = err.response?.data?.detail || err.message || 'Signup failed — please try again';
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 flex" data-testid="platform-signup-page">
      {/* Left panel */}
      <div className="hidden lg:flex lg:w-1/2 bg-[#E8678B] p-12 flex-col justify-between">
        <div>
          <div className="flex items-center gap-3 mb-16">
            <div className="w-10 h-10 rounded-xl bg-white/20 flex items-center justify-center">
              <span className="text-white font-bold text-lg">P</span>
            </div>
            <span className="text-white text-xl font-semibold" style={{ fontFamily: 'Outfit, sans-serif' }}>
              Pebbles for Platforms
            </span>
          </div>
          <h1 className="text-4xl font-bold text-white mb-4" style={{ fontFamily: 'Outfit, sans-serif' }}>
            Wallet infrastructure<br />in minutes, not months.
          </h1>
          <p className="text-white/80 text-lg max-w-md">
            Create wallets, track balances, and send transfers for your users.
            Start testing in your sandbox immediately — no approval needed.
          </p>
        </div>
        <div className="space-y-4">
          <div className="flex items-center gap-3 text-white/90">
            <div className="w-8 h-8 rounded-full bg-white/20 flex items-center justify-center text-sm">1</div>
            <span>Sign up and get sandbox API keys instantly</span>
          </div>
          <div className="flex items-center gap-3 text-white/90">
            <div className="w-8 h-8 rounded-full bg-white/20 flex items-center justify-center text-sm">2</div>
            <span>Build and test with fake banking data</span>
          </div>
          <div className="flex items-center gap-3 text-white/90">
            <div className="w-8 h-8 rounded-full bg-white/20 flex items-center justify-center text-sm">3</div>
            <span>Upload documents and go live when ready</span>
          </div>
        </div>
      </div>

      {/* Right panel */}
      <div className="flex-1 flex items-center justify-center p-6">
        <div className="w-full max-w-md">
          <h2 className="text-2xl font-bold text-gray-900 mb-2" style={{ fontFamily: 'Outfit, sans-serif' }}>
            Create your account
          </h2>
          <p className="text-gray-500 mb-8">Start building with wallet infrastructure today</p>

          <form onSubmit={handleSubmit} className="space-y-5" data-testid="signup-form">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1.5">Business name</label>
              <input
                type="text"
                required
                value={form.business_name}
                onChange={e => setForm({ ...form, business_name: e.target.value })}
                data-testid="signup-business-name"
                className="w-full px-4 py-2.5 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#E8678B]/20 focus:border-[#E8678B] transition-colors"
                placeholder="Alive Movie Mart"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1.5">Work email</label>
              <input
                type="email"
                required
                value={form.email}
                onChange={e => setForm({ ...form, email: e.target.value })}
                data-testid="signup-email"
                className="w-full px-4 py-2.5 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#E8678B]/20 focus:border-[#E8678B] transition-colors"
                placeholder="you@company.com"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1.5">Password</label>
              <div className="relative">
                <input
                  type={showPassword ? 'text' : 'password'}
                  required
                  minLength={8}
                  value={form.password}
                  onChange={e => setForm({ ...form, password: e.target.value })}
                  data-testid="signup-password"
                  className="w-full px-4 py-2.5 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#E8678B]/20 focus:border-[#E8678B] transition-colors pr-10"
                  placeholder="At least 8 characters"
                />
                <button type="button" onClick={() => setShowPassword(!showPassword)} className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400">
                  {showPassword ? <EyeOff size={16} /> : <Eye size={16} />}
                </button>
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1.5">What are you building?</label>
              <select
                value={form.business_type}
                onChange={e => { setForm({ ...form, business_type: e.target.value }); if (e.target.value !== 'other') setOtherType(''); }}
                data-testid="signup-business-type"
                className="w-full px-4 py-2.5 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#E8678B]/20 focus:border-[#E8678B] transition-colors bg-white"
              >
                <option value="" disabled>Select your industry</option>
                {businessTypes.map(bt => (
                  <option key={bt.value} value={bt.value}>{bt.label}</option>
                ))}
              </select>
              {form.business_type === 'other' && (
                <input
                  type="text"
                  value={otherType}
                  onChange={e => setOtherType(e.target.value)}
                  data-testid="signup-other-type"
                  className="w-full mt-2 px-4 py-2.5 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#E8678B]/20 focus:border-[#E8678B] transition-colors"
                  placeholder="Tell us what you're building..."
                  required
                />
              )}
            </div>

            <button
              type="submit"
              disabled={loading}
              data-testid="signup-submit-btn"
              className="w-full py-2.5 bg-[#E8678B] text-white rounded-lg text-sm font-semibold hover:bg-[#d15a7d] transition-colors flex items-center justify-center gap-2 disabled:opacity-50"
            >
              {loading ? 'Creating account...' : <>Create account <ArrowRight size={16} /></>}
            </button>
          </form>

          <p className="text-center text-sm text-gray-500 mt-6">
            Already have an account? <Link to="/platform/login" className="text-[#E8678B] font-medium hover:underline" data-testid="goto-login-link">Sign in</Link>
          </p>
        </div>
      </div>
    </div>
  );
}
